import React, { useEffect, useState } from 'react';
import { sendClientPriceList, ClientPriceList } from '../lib/clientPriceListApi';
import { getClientPriceListPublicLink } from '../lib/clientPriceListLink';
import {
  buildDefaultClientPriceListEmailDescription,
  ensureClientPriceListLinkPresent,
} from '../utils/clientPriceListEmailTemplate';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface ClientPriceListEmailDialogProps {
  open: boolean;
  list: ClientPriceList | null;
  onClose: () => void;
  /** Called after a successful send so the caller can refresh (status flips to Sent server-side). */
  onSent: () => void | Promise<void>;
  /** Bump above another open modal (e.g. the items popup on ClientPriceListsPage). */
  zIndexClassName?: string;
}

/**
 * Shared "Send Price List" compose dialog for both ClientPriceListDetailPage and
 * ClientPriceListsPage — single implementation so the email template, the client
 * link, and the send behavior can't drift between the two pages.
 */
export default function ClientPriceListEmailDialog({
  open,
  list,
  onClose,
  onSent,
  zIndexClassName = 'z-50',
}: ClientPriceListEmailDialogProps) {
  const [recipientEmail, setRecipientEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [sending, setSending] = useState(false);

  // The client's link carries the list's secret (D116.1), which only the staff
  // `/details` read returns: `list` must come from there, never from a list row.
  const token = list?.accessToken;

  useEffect(() => {
    if (!open || !list || !token) return;
    setRecipientEmail('');
    setSubject('');
    setDescription(
      buildDefaultClientPriceListEmailDescription({
        clientName: list.clientName || list.name,
        link: getClientPriceListPublicLink(list._id, token),
      })
    );
  }, [open, list, token]);

  if (!open || !list) return null;

  const handleRestoreDefault = () => {
    if (!token) return;
    setDescription(
      buildDefaultClientPriceListEmailDescription({
        clientName: list.clientName || list.name,
        link: getClientPriceListPublicLink(list._id, token),
      })
    );
  };

  const handleClose = () => {
    if (sending) return;
    onClose();
  };

  const handleSend = async () => {
    if (!token) {
      alert('This price list has no client link yet. Close this window, reopen the list and try again.');
      return;
    }
    if (!subject.trim()) {
      alert('Please enter a subject.');
      return;
    }
    if (!recipientEmail.trim() || !EMAIL_RE.test(recipientEmail.trim())) {
      alert('Please enter a valid recipient email address.');
      return;
    }
    setSending(true);
    try {
      const link = getClientPriceListPublicLink(list._id, token);
      const finalDescription = ensureClientPriceListLinkPresent(description.trim(), link);
      await sendClientPriceList(list._id, {
        recipientEmail: recipientEmail.trim(),
        subject: subject.trim(),
        description: finalDescription,
      });
      onClose();
      await onSent();
    } catch (e) {
      console.error('Failed to send price list:', e);
      alert(e instanceof Error ? e.message : 'Failed to send price list. Please check the recipient email and try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className={`fixed inset-0 ${zIndexClassName} flex items-center justify-center bg-[#222222]/48 p-4 modal-overlay-safe`}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div
        className="bg-white rounded-lg shadow-lg w-full max-w-[610px] flex flex-col overflow-hidden"
        style={{ fontFamily: "'Public Sans', sans-serif", borderRadius: 8 }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 pt-6 pb-2">
          <div>
            <h2 className="text-base font-semibold leading-6 text-slate-900">
              Send Price List
            </h2>
            <p className="text-sm leading-[22px] text-slate-500 mt-1">
              Name: {list.name} | Total Selected Items: {list.items?.length ?? 0}
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="w-6 h-6 flex items-center justify-center rounded text-slate-400 hover:bg-[#F9FAF9]"
            aria-label="Close"
          >
            <span className="text-xl leading-none">×</span>
          </button>
        </div>

        <div className="px-6 py-4 space-y-5">
          <div>
            <label className="block font-medium text-slate-900 mb-1.5" style={{ fontSize: 14, lineHeight: '22px' }}>
              Recipient Email
            </label>
            <input
              type="email"
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              placeholder="client@example.com"
              className="w-full h-10 px-3 border border-[#D7DBD9] rounded-md text-base leading-6 placeholder:text-slate-400 text-slate-900 focus:outline-none focus:ring-1 focus:ring-[#D7DBD9]"
            />
          </div>
          <div>
            <label className="block font-medium text-slate-900 mb-1.5" style={{ fontSize: 14, lineHeight: '22px' }}>
              Subject
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Enter Subject"
              className="w-full h-10 px-3 border border-[#D7DBD9] rounded-md text-base leading-6 placeholder:text-slate-400 text-slate-900 focus:outline-none focus:ring-1 focus:ring-[#D7DBD9]"
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block font-medium text-slate-900" style={{ fontSize: 14, lineHeight: '22px' }}>
                Description
              </label>
              <button
                type="button"
                onClick={handleRestoreDefault}
                className="text-xs font-semibold text-primary-500 hover:underline"
              >
                Restore Default
              </button>
            </div>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Write here..."
              rows={8}
              className="w-full px-3 py-2 border border-[#D7DBD9] rounded-md text-base leading-6 placeholder:text-slate-400 text-slate-900 focus:outline-none focus:ring-1 focus:ring-[#D7DBD9] resize-none"
            />
          </div>
        </div>

        <div className="px-6 pb-6 pt-2">
          <button
            type="button"
            onClick={handleSend}
            disabled={sending}
            className="w-full h-10 flex items-center justify-center rounded-lg font-semibold text-white bg-primary-500 hover:opacity-90 disabled:opacity-50"
            style={{ fontSize: 16, lineHeight: '24px', borderRadius: 8 }}
          >
            {sending ? 'Sending...' : 'Send price list'}
          </button>
        </div>
      </div>
    </div>
  );
}
